import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Package, ChevronDown, ChevronRight, Link } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Platform } from '../../../types/platform';
import { workerApi } from '../../../services/workerApi';

interface ProductsTabProps {
  account: Platform;
}

type Product = { name: string; description: string; price: string; image_urls: string[]; ref_url: string };

const migrateProduct = (p: any): Product => ({
  name: p.name || '',
  description: p.description || '',
  price: p.price || '',
  image_urls: Array.isArray(p.image_urls)
    ? p.image_urls
    : (p.image_url ? [p.image_url] : []),
  ref_url: p.ref_url || '',
});

export const ProductsTab: React.FC<ProductsTabProps> = ({ account }) => {
  const { t } = useTranslation('chatbot');
  const [products, setProducts] = useState<Product[]>([]);
  const [collapsedProducts, setCollapsedProducts] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    workerApi.getBotConfig(account.platform_id)
      .then((data: any) => {
        if (cancelled) return;
        setProducts(Array.isArray(data.products) ? data.products.map(migrateProduct) : []);
      })
      .catch(() => { /* worker unreachable — keep empty list */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [account.platform_id]);

  const addProduct = () => {
    // New product starts expanded
    setProducts((prev) => [...prev, { name: '', description: '', price: '', image_urls: [], ref_url: '' }]);
  };

  const removeProduct = (i: number) => {
    setCollapsedProducts((prev) => {
      const s = new Set(prev);
      s.delete(i);
      // Re-index: shift down all indices above i
      const shifted = new Set<number>();
      s.forEach((idx) => { if (idx < i) shifted.add(idx); else shifted.add(idx - 1); });
      return shifted;
    });
    setProducts((prev) => prev.filter((_, idx) => idx !== i));
  };

  const toggleProductCollapse = (i: number) =>
    setCollapsedProducts((prev) => {
      const s = new Set(prev);
      s.has(i) ? s.delete(i) : s.add(i);
      return s;
    });

  const updateProduct = (i: number, field: Exclude<keyof Product, 'image_urls'>, value: string) =>
    setProducts((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));

  const addImageUrl = (i: number) =>
    setProducts((prev) => prev.map((p, idx) => idx === i ? { ...p, image_urls: [...p.image_urls, ''] } : p));

  const removeImageUrl = (i: number, j: number) =>
    setProducts((prev) => prev.map((p, idx) => idx === i ? { ...p, image_urls: p.image_urls.filter((_, jdx) => jdx !== j) } : p));

  const updateImageUrl = (i: number, j: number, value: string) =>
    setProducts((prev) => prev.map((p, idx) => idx === i ? { ...p, image_urls: p.image_urls.map((u, jdx) => jdx === j ? value : u) } : p));

  const handleSave = async () => {
    setSaving(true);
    try {
      await workerApi.patchBotConfig(account.platform_id, {
        products: products.filter((p) => p.name.trim()),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('❌ Failed to save products:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pb-20">
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-50 bg-gray-50/50">
          <span className="text-gray-400"><Package size={14} /></span>
          <h3 className="text-sm font-semibold text-gray-700">{t('productsTitle')}</h3>
          <span className="ml-auto text-xs text-gray-400 font-normal">{t('productsCount', { count: products.length })}</span>
        </div>
        <div className="p-5">
          <p className="text-xs text-gray-400 mb-4 leading-relaxed">
            {t('productsDesc')}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-300 text-sm">
              {t('loading')}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {products.map((p, i) => {
                  const isCollapsed = collapsedProducts.has(i);
                  return (
                    <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                      {/* Product header — always visible */}
                      <div
                        className="flex items-center gap-2 px-3 py-2.5 bg-gray-50/60 cursor-pointer select-none hover:bg-gray-100/60 transition-colors"
                        onClick={() => toggleProductCollapse(i)}
                      >
                        <span className="text-gray-300 flex-shrink-0">
                          {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                        </span>
                        <span className="flex-1 text-xs font-medium text-gray-700 truncate">
                          {p.name || <span className="text-gray-300 font-normal">{t('productFallback', { index: i + 1 })}</span>}
                        </span>
                        {p.price && (
                          <span className="text-xs text-gray-400 shrink-0 mr-1">{p.price}</span>
                        )}
                        <button
                          type="button"
                          title="Xóa sản phẩm"
                          onClick={(e) => { e.stopPropagation(); removeProduct(i); }}
                          className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>

                      {/* Product fields — collapsible */}
                      {!isCollapsed && (
                        <div className="p-3 space-y-2 bg-gray-50/30">
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              value={p.name}
                              onChange={(e) => updateProduct(i, 'name', e.target.value)}
                              placeholder={t('productNamePlaceholder')}
                              className="px-2.5 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] bg-white"
                            />
                            <input
                              value={p.price}
                              onChange={(e) => updateProduct(i, 'price', e.target.value)}
                              placeholder={t('productPricePlaceholder')}
                              className="px-2.5 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] bg-white"
                            />
                          </div>
                          <textarea
                            value={p.description}
                            onChange={(e) => updateProduct(i, 'description', e.target.value)}
                            placeholder={t('productDescPlaceholder')}
                            rows={4}
                            className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] resize-y bg-white min-h-[80px]"
                          />
                          {/* Images */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-gray-500">{t('productImages')}</span>
                              <span className="text-xs text-gray-400">{t('productImagesNote')}</span>
                            </div>
                            {p.image_urls.map((url, j) => (
                              <div key={j} className="flex gap-2 items-center">
                                <input
                                  value={url}
                                  onChange={(e) => updateImageUrl(i, j, e.target.value)}
                                  placeholder={t('productImageUrlPlaceholder')}
                                  className="flex-1 px-2.5 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] bg-white"
                                />
                                {url && (
                                  <img
                                    src={url}
                                    alt={`${p.name} ${j + 1}`}
                                    className="w-10 h-10 rounded-md object-cover border border-gray-200 flex-shrink-0"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                )}
                                <button
                                  type="button"
                                  title="Xóa ảnh"
                                  onClick={() => removeImageUrl(i, j)}
                                  className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addImageUrl(i)}
                              className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#3390ec] font-medium transition-colors"
                            >
                              <Plus size={12} /> {t('productAddImage')}
                            </button>
                          </div>
                          <div className="flex gap-2 items-center">
                            <span className="text-gray-300 flex-shrink-0"><Link size={13} /></span>
                            <input
                              value={p.ref_url || ''}
                              onChange={(e) => updateProduct(i, 'ref_url', e.target.value)}
                              placeholder={t('productRefUrlPlaceholder')}
                              className="flex-1 px-2.5 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#3390ec] bg-white"
                            />
                            {p.ref_url && (
                              <a
                                href={p.ref_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#3390ec] hover:text-[#2b7dd6] flex-shrink-0 text-xs underline"
                                title={t('productOpenLink')}
                              >
                                {t('productOpenLink')}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addProduct}
                className="mt-3 flex items-center gap-1.5 text-xs text-[#3390ec] hover:text-[#2b7dd6] font-medium"
              >
                <Plus size={13} /> {t('addProduct')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm shadow-lg transition-all ${
            saved
              ? 'bg-emerald-500 text-white shadow-emerald-200'
              : saving || loading
              ? 'bg-gray-300 text-gray-500 cursor-wait shadow-none'
              : 'bg-[#3390ec] text-white hover:bg-[#2b7dd6] shadow-[#3390ec]/30'
          }`}
        >
          <Save size={15} />
          {saved ? t('saved') : saving ? t('saving') : loading ? t('loading') : t('saveProducts')}
        </button>
      </div>
    </div>
  );
};
